import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthCallback from "./components/AuthCallback";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import MemberLayout from "./pages/member/MemberLayout";
import Dashboard from "./pages/member/Dashboard";
import Deals from "./pages/member/Deals";
import DealDetail from "./pages/member/DealDetail";
import Interests from "./pages/member/Interests";
import Documents from "./pages/member/Documents";
import Transfers from "./pages/member/Transfers";
import Account from "./pages/member/Account";
import Distributions from "./pages/member/Distributions";
import Admin from "./pages/admin/Admin";
import AuthGoogle from "./pages/AuthGoogle";
import Calculator from "./pages/Calculator";
import MemberCalculator from "./pages/member/MemberCalculator";
import DealResponse from "./pages/DealResponse";
import Returns from "./pages/Returns";
import Membership from "./pages/Membership";
import DealStatus from "./pages/DealStatus";
import Pipeline from "./pages/member/Pipeline";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center">
    <span className="t-label">Solera</span>
  </div>
);

const Protected = ({ children, adminOnly }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/app" replace />;
  if (!adminOnly && !user.onboarded && user.role !== "admin") return <Navigate to="/onboarding" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/calculator" element={<Calculator />} />
      <Route path="/returns" element={<Returns />} />
      <Route path="/membership" element={<Membership />} />
      <Route path="/deal-status/:token" element={<DealStatus />} />
      <Route path="/deal-response/:token" element={<DealResponse />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/google" element={<AuthGoogle />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/app" element={<Protected><MemberLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="deals" element={<Deals />} />
        <Route path="deals/:dealId" element={<DealDetail />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="interests" element={<Interests />} />
        <Route path="documents" element={<Documents />} />
        <Route path="transfers" element={<Transfers />} />
        <Route path="account" element={<Account />} />
        <Route path="distributions" element={<Distributions />} />
        <Route path="calculator" element={<MemberCalculator />} />
      </Route>
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const TOAST_OPTIONS = {
  style: {
    background: "#0E1012", color: "#E8E6E1", border: "1px solid #24272B",
    borderRadius: 0, fontFamily: "'Instrument Sans', sans-serif",
  },
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="bottom-right" toastOptions={TOAST_OPTIONS} />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
